import { Loader } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectGrid } from './project-grid';
import { PROJECT_VIEW, type ProjectView } from './use-project-view';

/**
 * One full row of the grid at its widest for first load, and enough under the
 * loaded grid to read as "more is coming". The page itself fetches fifty.
 *
 * It is a full row at every step but `lg`, where three columns leave the fourth
 * placeholder alone on a second row. Left alone: hiding it in that one band
 * costs a breakpoint-range variant on a single child, and the label below the
 * block carries the meaning either way.
 */
const PLACEHOLDER_COUNT = 4;

/**
 * Not `bg-secondary`, which is the hover fill on both ProjectCard and
 * ProjectListCard: a placeholder in it is indistinguishable from a hovered card,
 * and while scrolling these appear right under the row the pointer is near.
 */
const FILL = 'bg-[var(--ds-color-bg-tertiary)]';

/** `motion-safe`, matching facade's own Skeleton. */
const PULSE = 'motion-safe:animate-pulse';

/**
 * Sized from the shipped ProjectCard, not from its frame — the frame draws a
 * borderless tile inset 16px inside the card's box, and a placeholder a
 * different shape from the card it stands in for makes the swap visible. Bars
 * sit inside line boxes of 20 and 16 so the line boxes set the height and the
 * bar thickness stays free.
 *
 * No avatar row, which the frame ends each tile with: those three circles stand
 * in for the project people stack, cut from v1, so the loaded card has nothing
 * there to stand in for.
 */
const SkeletonCard: FC = () => (
  <div
    className="overflow-hidden rounded-[var(--ds-radius-8)] border border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)]"
    data-testid="project-card-placeholder"
  >
    <div className={`aspect-[10/7] w-full ${FILL} ${PULSE}`} />
    <div className="flex flex-col gap-1 px-4 py-3">
      <div className="flex h-5 items-center">
        <div className={`h-3 w-full rounded-full ${FILL} ${PULSE}`} />
      </div>
      <div className="flex h-4 items-center">
        <div className={`h-2 w-3/5 rounded-full ${FILL} ${PULSE}`} />
      </div>
    </div>
  </div>
);

/**
 * Transcribed from ProjectListCard.css, not designed. The ground stays white
 * rather than filled — a solid bar the width of the row reads as a hovered row —
 * and the height comes from the thumbnail, not the body centred beside it.
 *
 * The thumbnail is 120px because the DS composes it as space-28 + space-2 —
 * neither 120 nor the 84 its 10/7 gives is on the spacing scale. Bars sit in
 * line boxes of 24 and 16, the row's own title and subtitle.
 */
const SkeletonRow: FC = () => (
  <div
    className="flex items-center gap-4 border-b border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-4"
    data-testid="project-row-placeholder"
  >
    <div
      className={`aspect-[10/7] w-[120px] shrink-0 rounded-[var(--ds-radius-8)] ${FILL} ${PULSE}`}
    />
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="flex h-6 items-center">
        <div className={`h-3 w-1/3 rounded-full ${FILL} ${PULSE}`} />
      </div>
      <div className="flex h-4 items-center">
        <div className={`h-2 w-1/5 rounded-full ${FILL} ${PULSE}`} />
      </div>
    </div>
  </div>
);

/** The placeholders themselves, one shape per view. */
const SkeletonPlaceholders: FC<{ view: ProjectView }> = ({ view }) => (
  <>
    {Array.from({ length: PLACEHOLDER_COUNT }, (_, index) =>
      view === PROJECT_VIEW.LIST ? <SkeletonRow key={index} /> : <SkeletonCard key={index} />,
    )}
  </>
);

interface LoadingLabelProps {
  /**
   * Off by default. The first-load arm also covers every search and filter
   * change, so announcing there would read "Loading projects" on each keystroke
   * and then the result, which ProjectListEmpty already announces on its own.
   * Visible text is still the point — it is the indicator that survives
   * `prefers-reduced-motion`, where the placeholders stop pulsing.
   */
  announce?: boolean;
}

/**
 * Follows the auth screen's loader, on DS tokens rather than facade's palette.
 *
 * Centred under whatever it is waiting on, and the same treatment in both
 * states: first load and infinite scroll are the same wait, and differ only in
 * whether they announce.
 */
const LoadingLabel: FC<LoadingLabelProps> = ({ announce }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center py-8">
      <div className="flex items-center gap-4" role={announce ? 'status' : undefined}>
        {/* Not behind motion-reduce: a frozen loading indicator reads as broken.
            It is also the only motion left under `reduce`, since the
            placeholders' pulse is motion-safe. */}
        <Loader
          aria-hidden
          className="animate-spin text-[var(--ds-color-text-secondary)]"
          size={24}
          strokeWidth={1.75}
        />
        <span className="text-base leading-6 text-[var(--ds-color-text-primary)]">
          {t('project-list.loading-title')}
        </span>
      </div>
    </div>
  );
};

/**
 * Nothing on screen yet: the grid's own tracks, filled with placeholders, with
 * the loader below them. Built on ProjectGrid rather than a grid of its own so
 * the placeholders cannot drift from the column steps the real cards use, and it
 * follows the persisted view for the same reason — a card grid that becomes rows
 * on load is a jump.
 *
 * The frame floats the loader in a pill over the middle of the row. Below the
 * placeholders instead, which is what infinite scroll already does: the label
 * stays in the flow, legible against the page rather than against whatever it
 * covers, and one treatment serves both waits.
 *
 * `aria-busy` rather than a live region, per the announce note above: it marks
 * the region as in flux without interrupting on every keystroke.
 */
export const ProjectListLoading: FC<{ view: ProjectView }> = ({ view }) => (
  <div aria-busy>
    <ProjectGrid projects={[]} view={view}>
      <SkeletonPlaceholders view={view} />
    </ProjectGrid>

    <LoadingLabel />
  </div>
);

/**
 * The same placeholders, passed into the loaded ProjectGrid as children so they
 * land on the same tracks as the cards above. A sibling grid would start a fresh
 * row: fifty per page leaves two cards in the last row at both three and four
 * columns.
 *
 * A wrapper rather than an alias of SkeletonPlaceholders, so the two keep their
 * own names in a component stack.
 */
export const ProjectListLoadingMore: FC<{ view: ProjectView }> = ({ view }) => (
  <SkeletonPlaceholders view={view} />
);

/**
 * Below the grid, which is also where first load puts it. The frame draws the
 * placeholder row in isolation, where over-the-row and below-the-row are the
 * same place; with a partial last row they are not.
 *
 * Announces, unlike first load: each fetch is a fresh wait the reader cannot see
 * coming, and it fires once per page rather than once per keystroke.
 */
export const ProjectListLoadingMoreLabel: FC = () => <LoadingLabel announce />;
